import { yupResolver } from '@hookform/resolvers/yup'
import React, { useContext, useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import Select from 'react-select'
import { Button, Form } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { getAllRegion } from '../../redux/slices/regionSlice'
import {
  addNewService,
  addPricingTable,
  editPricingTable,
  getAllService,
  getDefaultPricing,
  getPricingTableByID
} from '../../redux/slices/pricingTableSlice'
import { errorToast, successToast } from '../../util/Toast/toast'
import { pricingTableSchema } from '../../util/validationSchema'
import { SpinnerContext } from '../../context/spinnerContext'
import DynamicTableForm from './dynamicTable'

const NewTable = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pricing_table_id } = useParams()
  const { changeLoader } = useContext(SpinnerContext)
  const { allRegion } = useSelector(({ region }) => region)
  const { pricingData, allService } = useSelector(({ pricingTable }) => pricingTable)
  const [isCreateServiceField, setIsCreateServiceField] = useState(false)
  const [isRefresh, setIsRefresh] = useState(false)

  const defaultValues = {
    name: '',
    region_id: null,
    service_id: null,
    pricing_type: 'square_feet',
    pricingTableDetails: [
      {
        pricing_table_detail_id: '',
        bathroom: null,
        bedroom: null,
        square_feet: null,
        price: null,
        exceed_price: null,
        cleaner_payout: null,
        avg_time: null
      }
    ]
  }

  const updatedValue =
    pricing_table_id && pricingData
      ? {
          name: pricingData?.name || '',
          region_id: pricingData?.region ? { region_id: pricingData.region.region_id, region: pricingData.region.region } : null,
          service_id: pricingData?.service ? { service_id: pricingData.service.service_id, service: pricingData.service.service } : null,
          pricing_type: pricingData?.pricing_type || '',
          is_default: pricingData?.is_default || false,
          pricingTableDetails:
            pricingData?.pricing_table_detail?.map((data) => ({
              pricing_table_detail_id: data?.pricing_table_detail_id || '',
              bathroom: data?.bathroom || 0,
              bedroom: data?.bedroom || 0,
              square_feet: data?.square_feet || 0,
              price: data?.price || 0,
              exceed_price: data?.exceed_price || 0,
              cleaner_payout: data?.cleaner_payout || 0,
              avg_time: data?.avg_time || 0
            })) || []
        }
      : defaultValues

  const methods = useForm({
    resolver: yupResolver(pricingTableSchema),
    mode: 'all',
    defaultValues: updatedValue
  })

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    setValue,
    clearErrors,
    formState: { errors }
  } = methods

  useEffect(() => {
    reset(pricing_table_id ? updatedValue : defaultValues)
  }, [pricing_table_id, pricingData, reset])

  const onSubmit = (data) => {
    const formattedData = data?.pricingTableDetails?.map((item) => ({
      ...item,
      cleaner_payout: `${String(item.cleaner_payout)}`, // Add % back after removing any existing %
      avg_time: `${String(item.avg_time)}` // Add h back after removing any existing h
    }))
    const payload = {
      ...data,
      region_id: data.region_id?.region_id,
      service_id: data.service_id?.service_id,
      pricingTableDetails: formattedData
    }
    delete payload.serviceName
    changeLoader(true)

    const action = pricing_table_id ? editPricingTable({ id: pricing_table_id, data: payload }) : addPricingTable(payload)

    dispatch(action)
      .then(({ payload, error }) => {
        changeLoader(false)
        if (payload?.status === 200) {
          successToast(payload?.data?.status_message || 'Success!')
          navigate('/pricing')
          reset()
        } else {
          errorToast(payload?.data?.status_message || error?.message)
          changeLoader(false)
        }
      })
      .catch((err) => console.error('Error:', err))
  }

  useEffect(() => {
    dispatch(getAllRegion())
    if (pricing_table_id) {
      dispatch(getPricingTableByID(pricing_table_id))
    }
  }, [pricing_table_id])

  useEffect(() => {
    dispatch(getAllService({ is_pagination: false }))
  }, [isRefresh])

  const handleCreateService = async (service, err) => {
    setIsRefresh(false)
    if (service && !err) {
      const { payload } = await dispatch(addNewService({ service }))
      if (payload?.status === 200) {
        successToast(payload?.status_message || 'Service Added!')
        setIsRefresh(true)
        setIsCreateServiceField(false)
      }
    }
  }

  const importDefaultPricing = async () => {
    changeLoader(true)
    await dispatch(getDefaultPricing())
      .then((response) => {
        const res = response?.payload?.data?.data
        const newPricingTableDetails = res?.pricing_table_detail.map((data) => ({
          pricing_table_detail_id: data.pricing_table_detail_id || '',
          bathroom: data?.bathroom || 0,
          bedroom: data?.bedroom || 0,
          square_feet: data?.square_feet || 0,
          price: data?.price || 0,
          exceed_price: data?.exceed_price || 0,
          cleaner_payout: data?.cleaner_payout || 0,
          avg_time: data?.avg_time || 0
        }))
        setValue('pricingTableDetails', newPricingTableDetails)

        changeLoader(false)
      })
      .catch(() => {
        changeLoader(false)
      })
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="title_header">
          <h5 className="sec_title_text d-flex align-items-center" onClick={() => navigate('/pricing')}>
            <i className="fa fa-angle-left fs-5 me-2" aria-hidden="true"></i>
            Back to Pricing Table
          </h5>
        </div>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="common_card_div mb-4">
              <div className="row">
                <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6 col-xl-6">
                  <div className="form-group">
                    <label>
                      Title<span className="text-danger"> *</span>
                    </label>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <>
                          <input
                            className="form-control"
                            value={value}
                            type="text"
                            placeholder="Enter Title"
                            onChange={(val) => {
                              onChange(val)
                              trigger('name')
                            }}
                          />
                          <small className="text-danger">{errors.name?.message}</small>
                        </>
                      )}
                      defaultValue=""
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6 col-xl-6">
                  <div className="input_icon_box">
                    {isCreateServiceField ? (
                      <div className="form-group w-100">
                        <label>Service Name</label>
                        <Controller
                          name="serviceName"
                          control={control}
                          render={({ field: { onChange } }) => (
                            <>
                              <input
                                className="form-control"
                                autoComplete="off"
                                onChange={(value) => {
                                  onChange(value)
                                  trigger('service_id')
                                }}
                                placeholder="Add New Service Name"
                                onBlur={(e) => handleCreateService(e.target.value, errors?.serviceName?.message)}
                              />
                            </>
                          )}
                        />
                        <small className="text-danger">{errors?.serviceName?.message}</small>
                      </div>
                    ) : (
                      <div className="form-group input_text border-remove w-100">
                        <label>
                          Service Name<span className="text-danger"> *</span>
                        </label>
                        <Controller
                          name="service_id"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              className="react-selectcomponent form-control select-box-filed"
                              getOptionLabel={(option) => option?.service || ''}
                              getOptionValue={(option) => option?.service_id || ''}
                              options={allService || []}
                              isClearable
                              isSearchable
                              placeholder="Select Service"
                              classNamePrefix="select_kanakku"
                              onChange={(option) => {
                                field.onChange(option || null)
                                trigger('service_id')
                              }}
                            />
                          )}
                        />
                        <small className="text-danger">{errors?.service_id?.message}</small>
                      </div>
                    )}
                    <div className="icon_box_div add_icon">
                      <i
                        className={`fas ${isCreateServiceField ? 'fa-times' : 'fa-edit'} text-white`}
                        style={{ cursor: 'pointer', textAlign: 'center' }}
                        onClick={() => {
                          setIsCreateServiceField((prev) => !prev)
                          setValue('serviceName', '')
                          clearErrors('serviceName')
                        }}
                      ></i>
                    </div>
                  </div>
                </div>

                <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-3">
                  <div className="form-group input_text border-remove">
                    <label>Region Default {!updatedValue?.is_default && <span className="text-danger"> *</span>}</label>
                    <Controller
                      name="region_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          className={`react-selectcomponent form-control select-box-filed`}
                          getOptionLabel={(option) => `${option.region}`}
                          getOptionValue={(option) => `${option.region_id}`}
                          options={allRegion || []}
                          isClearable
                          isSearchable={true}
                          placeholder={`Select Region`}
                          classNamePrefix="select_kanakku"
                          isDisabled={updatedValue?.is_default}
                          onChange={(option) => {
                            field.onChange(option || null)
                            trigger('region_id')
                            // setValue('selectAll', false)
                          }}
                        />
                      )}
                    />
                    <small className="text-danger">{errors?.region_id?.message}</small>
                  </div>
                </div>
                <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-3">
                  <div className="form-group input_text border-remove">
                    <label>
                      Pricing Type <span className="text-danger"> *</span>
                    </label>
                    <div className="d-flex gap-3 gap-md-5 gap-lg-5 gap-xl-3 gap-xxl-5">
                      <Controller
                        name="pricing_type"
                        control={control}
                        render={({ field }) => (
                          <>
                            {['flat', 'square_feet']?.map((option, index) => (
                              <Form.Check
                                key={index}
                                type="switch"
                                id="custom-switch"
                                label={option}
                                {...field}
                                checked={field.value === option} // ensures checked state is bound to value
                                onChange={() => {
                                  // handleSwitchChange(option)
                                  if (field.value == 'flat') {
                                    field.onChange('square_feet')
                                  } else {
                                    field.onChange('flat')
                                  }
                                  trigger('pricing_type')
                                }}
                              />
                            ))}
                          </>
                        )}
                      />
                    </div>
                    <small className="text-danger">{errors?.pricing_type?.message}</small>
                  </div>
                </div>
              </div>
              <div className="common_card_div mb-4">
                {!pricing_table_id && (
                  <div className="d-flex" style={{ justifyContent: 'flex-end', paddingBottom: '5px' }}>
                    <Button className="Checklist_btn" onClick={() => importDefaultPricing()}>
                      Import Default Pricing
                    </Button>
                  </div>
                )}
                <div className="row">
                  <DynamicTableForm />
                </div>
              </div>
              <Button type="submit" className="btn btn-small toggle-button-sky sky_btn">
                {pricing_table_id ? 'Update' : 'Save'} Pricing Table
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  )
}

export default NewTable
